import { useCallback, useEffect, useRef, useState } from "react";

import { defaultIsOnline } from "../capture/use-offline-queue";
import {
  cancelOpenShiftRequest,
  claimOpenShiftSlot,
  fetchMyOpenShiftRequests,
  fetchOpenShifts,
  indexRequestsBySlot,
  requestOpenShiftSlot,
  type OpenShiftRpcClient,
  type OpenShiftRow,
  type OpenShiftsQueryClient,
} from "./open-shifts";
import {
  COLOR_TOKEN_COPY,
  OPEN_SHIFT_REQUEST_TEXT_MAX,
} from "../../../../packages/shared/src/schedule-color.ts";
import type { OpenShiftRequest } from "../../../../packages/shared/src/open-shift-requests.ts";

// Open Shifts (G6, ship behind web_my_shifts alongside My Shifts). The open-staffing board:
// published, future, eligible slots a guard can take. Online-only by design (same honesty as
// My Shifts / timeclock). No guard ids ever render.
//
// Wave 6A made "take this seat" two different promises, and the difference matters to the
// person standing in a parking lot deciding whether they are working Saturday:
//   instant_claim    -> Claim. The shift is yours; it moves to My shifts.
//   request_approval -> Request. Nothing is yours until a supervisor approves.
// The row's own `open_fill_mode` picks the verb, so the guard is never offered "Claim" for a
// seat that only a supervisor can grant. The server refuses the wrong verb regardless.
//
// Template color renders as a keyline plus a NAMED chip - a label for the shift type, never
// a status signal, and never the raw token string as a style value.

type PanelClient = OpenShiftsQueryClient & OpenShiftRpcClient;

function formatWindow(startsAt: string | null, endsAt: string | null): string {
  if (startsAt === null || endsAt === null) {
    return "Time not set";
  }
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = (date: Date) => date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  const crossesDay = start.toDateString() !== end.toDateString();
  return `${day} - ${time(start)} to ${time(end)}${crossesDay ? " +1" : ""}`;
}

function humanizeCredential(value: string): string {
  const words = value.replaceAll("_", " ").trim();
  return words.length === 0 ? value : words[0]!.toUpperCase() + words.slice(1);
}

interface BoardState {
  rows: OpenShiftRow[];
  pendingBySlot: Map<string, OpenShiftRequest>;
  rejectedRequests: OpenShiftRequest[];
  /** The pending-request check failed; the board cannot claim the guard has none waiting. */
  pendingCheckFailed: boolean;
}

/** Shown when the connection dropped between render and tap. */
export const OFFLINE_ACTION_COPY =
  "You are offline - that did not go through. Get back online and try again.";
export const PENDING_CHECK_FAILED_COPY =
  "Could not check whether you have request updates. Refresh to see them.";
export const MISSING_REJECTION_REASON_COPY = "The desk did not include a reason.";

function formatReviewedAt(reviewedAt: string | null): string | null {
  if (reviewedAt === null) return null;
  const reviewed = new Date(reviewedAt);
  if (Number.isNaN(reviewed.getTime())) return null;
  return reviewed.toLocaleString(undefined, {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  });
}

export function OpenShiftsPanel({
  client,
  accountId,
  isOnline = defaultIsOnline,
}: {
  client: PanelClient;
  accountId: string;
  /** Test seam; defaults to navigator.onLine. */
  isOnline?: () => boolean;
}) {
  const [board, setBoard] = useState<BoardState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [confirmFor, setConfirmFor] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dismissedRejectedRequestKeys, setDismissedRejectedRequestKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const openSeatsHeading = useRef<HTMLHeadingElement>(null);
  const online = isOnline();

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const loaded = await fetchOpenShifts(client, accountId);
      const requests = await fetchMyOpenShiftRequests(client, accountId);
      if (!mounted.current) return;
      setBoard({
        rows: loaded.rows,
        // `loaded.degraded` is deliberately NOT surfaced here. The brief allows the
        // older-server note only "if useful", and to a guard it is not: a pre-6A server has
        // no request-approval templates at all, so "Claim" is the correct and only verb, and
        // absent color is invisible rather than wrong. The flag stays on the data layer,
        // where it is tested and where a desk-side reader can still use it.
        pendingBySlot: indexRequestsBySlot(requests.requests),
        rejectedRequests: requests.requests.filter((request) => request.status === "rejected"),
        pendingCheckFailed: requests.failed,
      });
      setLoadFailed(false);
    } catch {
      if (mounted.current) setLoadFailed(true);
    } finally {
      inFlight.current = false;
    }
  }, [client, accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  function closeConfirm(): void {
    setConfirmFor(null);
    setNote("");
  }

  async function runAction(
    slotId: string,
    action: () => Promise<Awaited<ReturnType<typeof claimOpenShiftSlot>>>,
  ): Promise<void> {
    // Connectivity is read at render, so a guard who loses signal with the panel open can
    // still tap. Re-check here: the alternative is a write failure reported as
    // "it may have just filled up", which tells them the seat is gone when it is not.
    if (!isOnline()) {
      setActionError(OFFLINE_ACTION_COPY);
      return;
    }
    setPending(slotId);
    setActionError(null);
    const result = await action();
    if (!mounted.current) return;
    setPending(null);
    if (!result.ok) {
      setActionError(result.message);
      // A stale board means the desk changed something under this guard - refetch so the
      // row shows the verb that will actually work.
      if (result.stale) await load();
      return;
    }
    closeConfirm();
    await load();
  }

  if (loadFailed) {
    return (
      <section className="myshifts" aria-label="Open shifts">
        <h2 className="myshifts__title">Open shifts</h2>
        <p className="myshifts__state" role="alert">Could not load open shifts. Try again later.</p>
      </section>
    );
  }
  if (board === null) {
    return (
      <section className="myshifts" aria-label="Open shifts">
        <h2 className="myshifts__title">Open shifts</h2>
        <p className="myshifts__state" role="status">Loading open shifts...</p>
      </section>
    );
  }

  const { rows, pendingBySlot, pendingCheckFailed, rejectedRequests } = board;
  const visibleRejectedRequests = rejectedRequests.filter(
    (request) => !dismissedRejectedRequestKeys.has(`${accountId}:${request.requestId}`),
  );
  // A request whose slot has left the open board - usually because the seat filled while the
  // guard waited. Without this the ask is invisible AND uncancellable from the phone, so it
  // sits pending forever with the guard assuming it lapsed.
  const boardSlots = new Set(rows.map((row) => row.shiftSlotId));
  const orphanPending = [...pendingBySlot.values()].filter(
    (request) => request.shiftSlotId !== null && !boardSlots.has(request.shiftSlotId),
  );
  return (
    <section className="myshifts" aria-label="Open shifts">
      <h2 className="myshifts__title" ref={openSeatsHeading} tabIndex={-1}>Open shifts</h2>
      {!online ? (
        <p className="myshifts__offline" role="status">
          You are offline - open shifts are shown, but taking one needs you back online.
        </p>
      ) : null}
      {actionError ? (
        <p className="myshifts__state myshifts__state--error" role="alert">{actionError}</p>
      ) : null}
      {pendingCheckFailed ? (
        <p className="myshifts__state" role="status">{PENDING_CHECK_FAILED_COPY}</p>
      ) : null}
      {visibleRejectedRequests.length > 0 ? (
        <section className="myshifts__request-updates" aria-label="My requests">
          <h3 className="myshifts__subtitle">My requests</h3>
          <ul className="myshifts__list">
            {visibleRejectedRequests.map((request) => {
              const reviewedAt = formatReviewedAt(request.reviewedAt);
              return (
                <li
                  className={`myshifts__declined-request tpl-color--${request.colorToken}`}
                  key={request.requestId}
                >
                  <div className="myshifts__declined-head">
                    <span className="tpl-color">
                      <span className="tpl-color__dot" aria-hidden="true" />
                      {COLOR_TOKEN_COPY[request.colorToken]}
                    </span>
                    <span className="myshifts__declined-status">Declined</span>
                  </div>
                  <h4 className="myshifts__declined-title">Request declined</h4>
                  <p className="myshifts__where">
                    {request.siteName ?? "Site"}
                    {request.routeName ? ` - ${request.routeName}` : ""}
                  </p>
                  <p className="myshifts__when">
                    {formatWindow(request.slotStartsAt, request.slotEndsAt)}
                  </p>
                  <div className="myshifts__declined-reason">
                    <p className="myshifts__declined-label">From the desk</p>
                    <p>{request.rejectionReason ?? MISSING_REJECTION_REASON_COPY}</p>
                  </div>
                  {reviewedAt !== null ? (
                    <p className="myshifts__declined-reviewed">Declined {reviewedAt}</p>
                  ) : null}
                  <div className="myshifts__declined-actions">
                    <button
                      className="myshifts__btn myshifts__btn--primary"
                      onClick={() => {
                        openSeatsHeading.current?.focus({ preventScroll: true });
                        openSeatsHeading.current?.scrollIntoView({ block: "start" });
                      }}
                      type="button"
                    >
                      See open seats
                    </button>
                    <button
                      className="myshifts__btn"
                      onClick={() => {
                        setDismissedRejectedRequestKeys((current) => {
                          const next = new Set(current);
                          next.add(`${accountId}:${request.requestId}`);
                          return next;
                        });
                      }}
                      type="button"
                    >
                      Dismiss
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      {rows.length === 0 ? (
        <p className="myshifts__state" role="status">
          No open shifts right now. Shifts your supervisor leaves unassigned show up here to take.
        </p>
      ) : (
        <ul className="myshifts__list">
          {rows.map((shift) => {
            const request = pendingBySlot.get(shift.shiftSlotId) ?? null;
            const requestApproval = shift.openFillMode === "request_approval";
            const busy = pending === shift.shiftSlotId;
            return (
              <li
                key={shift.shiftSlotId}
                className={`myshifts__card myshifts__card--keyed tpl-color--${shift.colorToken}`}
              >
                <p className="myshifts__where">
                  <span className="tpl-color">
                    <span className="tpl-color__dot" aria-hidden="true" />
                    {COLOR_TOKEN_COPY[shift.colorToken]}
                  </span>{" "}
                  {shift.siteName ?? "Site"}
                  {shift.routeName ? ` - ${shift.routeName}` : ""}
                </p>
                <p className="myshifts__when">{formatWindow(shift.startsAt, shift.endsAt)}</p>
                {shift.requiredCredentialTypes.length > 0 ? (
                  <p className="myshifts__creds">
                    {shift.requiredCredentialTypes.map((type) => (
                      <span key={type} className="myshifts__cred">{humanizeCredential(type)}</span>
                    ))}
                  </p>
                ) : null}

                {request !== null ? (
                  <div className="myshifts__pending">
                    <p className="myshifts__pending-copy" role="status">
                      Requested - waiting for a supervisor to approve. The shift is not yours yet.
                    </p>
                    <button
                      type="button"
                      className="myshifts__btn"
                      disabled={!online || busy}
                      onClick={() =>
                        void runAction(shift.shiftSlotId, () =>
                          cancelOpenShiftRequest(client, accountId, request.requestId),
                        )
                      }
                    >
                      Cancel request
                    </button>
                  </div>
                ) : confirmFor === shift.shiftSlotId ? (
                  <div
                    className="myshifts__confirm"
                    role="dialog"
                    aria-label={`${requestApproval ? "Request" : "Claim"} shift - ${shift.siteName ?? "site"}`}
                  >
                    <p className="myshifts__confirm-copy">
                      {requestApproval
                        ? "Ask for this shift? A supervisor has to approve it - it is not yours until they do."
                        : "Claim this shift? It moves to My shifts and you are expected to work it."}
                    </p>
                    {requestApproval ? (
                      <label className="myshifts__note">
                        Note for your supervisor (optional)
                        <textarea
                          rows={2}
                          value={note}
                          maxLength={OPEN_SHIFT_REQUEST_TEXT_MAX}
                          onChange={(event) => setNote(event.target.value)}
                        />
                      </label>
                    ) : null}
                    <div className="myshifts__confirm-actions">
                      <button
                        type="button"
                        className="myshifts__btn"
                        disabled={busy}
                        onClick={closeConfirm}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="myshifts__btn myshifts__btn--primary"
                        disabled={!online || busy}
                        onClick={() =>
                          void runAction(shift.shiftSlotId, () =>
                            requestApproval
                              ? requestOpenShiftSlot(client, accountId, shift.shiftSlotId, note)
                              : claimOpenShiftSlot(client, accountId, shift.shiftSlotId),
                          )
                        }
                      >
                        {requestApproval ? "Send request" : "Confirm claim"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="myshifts__btn myshifts__btn--primary"
                    disabled={!online || pending !== null}
                    onClick={() => {
                      setActionError(null);
                      setNote("");
                      setConfirmFor(shift.shiftSlotId);
                    }}
                  >
                    {requestApproval ? "Request shift" : "Claim shift"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {orphanPending.length > 0 ? (
        <div className="myshifts__orphans">
          <h3 className="myshifts__subtitle">Requests waiting</h3>
          <p className="myshifts__state" role="status">
            You asked for these and they are no longer on the open board - usually because the
            seat filled. They are still waiting on a supervisor until you cancel them.
          </p>
          <ul className="myshifts__list">
            {orphanPending.map((request) => (
              <li
                key={request.requestId}
                className={`myshifts__card myshifts__card--keyed tpl-color--${request.colorToken}`}
              >
                <p className="myshifts__where">
                  <span className="tpl-color">
                    <span className="tpl-color__dot" aria-hidden="true" />
                    {COLOR_TOKEN_COPY[request.colorToken]}
                  </span>{" "}
                  {request.siteName ?? "Site"}
                  {request.routeName ? ` - ${request.routeName}` : ""}
                </p>
                <p className="myshifts__when">
                  {formatWindow(request.slotStartsAt, request.slotEndsAt)}
                </p>
                <button
                  type="button"
                  className="myshifts__btn"
                  disabled={!online || pending === request.requestId}
                  onClick={() =>
                    void runAction(request.requestId, () =>
                      cancelOpenShiftRequest(client, accountId, request.requestId),
                    )
                  }
                >
                  Cancel request
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
